import { useId, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { CalendarIcon, ChevronDown, PlusIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { SharedLinkTypeTooltip } from './shared-link-type-tooltip';
import {
  defaultFormValues,
  getVisibleShareSettings,
  type DigiTwinShareSubmitMeta,
  type DigiTwinShareValues,
  type DigiTwinShareMode,
  type ShareSettingKey,
} from './shared-link-settings';

export function ShareLinkForm({
  mode,
  defaultValues,
  onShare,
  onCancel,
  pending,
  initialName,
}: {
  mode: DigiTwinShareMode;
  defaultValues?: DigiTwinShareValues;
  initialName?: string;
  onShare: (values: DigiTwinShareValues, meta: DigiTwinShareSubmitMeta) => void;
  onCancel: () => void;
  pending?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const settingsId = useId();
  const expirationChanged = useRef(false);
  const [settingsOpened, setSettingsOpened] = useState(false);
  const [calendarOpened, setCalendarOpened] = useState(false);
  const { register, control, watch, getValues, setValue, handleSubmit } =
    useForm<DigiTwinShareValues>({
      defaultValues: defaultValues || { ...defaultFormValues, name: initialName || '' },
    });
  const values = watch();
  const visibleSettings = getVisibleShareSettings(values.editingMode);
  const enabledCount = visibleSettings.filter((key) => values[key]).length;
  const expiresIn = Number(values.expiresIn);
  const calculatedExpiry = dayjs().startOf('day').add(expiresIn, 'day');
  const expiryDate =
    Number.isFinite(expiresIn) && expiresIn >= 1 && calculatedExpiry.isValid()
      ? calculatedExpiry.toDate()
      : undefined;
  const expiresInRegistration = register('expiresIn');

  const updateSetting = (key: ShareSettingKey, checked: boolean) => {
    setValue(key, checked);
    setValue(
      'settings',
      visibleSettings.every((option) => getValues(option) === true)
    );
  };

  return (
    <form
      onSubmit={handleSubmit((formValues) =>
        onShare(
          { ...formValues, name: formValues.name.trim() },
          { expirationChanged: expirationChanged.current }
        )
      )}
      className="mt-5 space-y-5"
    >
      <fieldset disabled={pending} className="space-y-5">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Input
            {...register('name')}
            label={t('sharedLinkName')}
            maxLength={200}
            size="sm"
            autoFocus
            onFocus={(event) => event.currentTarget.select()}
          />
          <div>
            <div className="mb-2 flex items-center gap-1 text-xs text-text-primary">
              {t('type')}
              <SharedLinkTypeTooltip />
            </div>
            <fieldset
              className="grid grid-cols-2 rounded-md bg-muted p-1"
              aria-label={t('editing-mode')}
            >
              {(['view', 'limited'] as const).map((mode) => (
                <Button
                  key={mode}
                  variant="ghost"
                  size="sm"
                  className={cn(
                    'h-8 !px-2 text-sm',
                    values.editingMode === mode && 'bg-primary-lighter shadow-sm'
                  )}
                  aria-pressed={values.editingMode === mode}
                  onClick={() => {
                    setValue('editingMode', mode);
                    setValue(
                      'settings',
                      getVisibleShareSettings(mode).every((key) => getValues(key) === true)
                    );
                  }}
                >
                  {t(mode === 'view' ? 'view-only' : 'limited')}
                </Button>
              ))}
            </fieldset>
          </div>
        </div>
        <div>
          <div className="mb-2 text-xs text-text-primary">{t('expires-in')}</div>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <Input
              {...expiresInRegistration}
              onChange={(event) => {
                expirationChanged.current = true;
                expiresInRegistration.onChange(event);
              }}
              type="number"
              min={1}
              step={1}
              required
              size="sm"
              wrapperClassName="w-20"
              aria-label={t('expires-in')}
            />
            <span>{t('sharedLinkDays')}</span>
            <span aria-hidden>—</span>
            <Popover open={calendarOpened} onOpenChange={setCalendarOpened}>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="gap-2 !px-2 text-sm"
                  aria-label={t('expires-at')}
                >
                  {expiryDate
                    ? new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }).format(
                        expiryDate
                      )
                    : '—'}
                  <CalendarIcon className="!h-4 !w-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={expiryDate}
                  defaultMonth={expiryDate}
                  fixedRowCount={6}
                  disabled={{ before: dayjs().add(1, 'day').startOf('day').toDate() }}
                  onSelect={(date) => {
                    if (!date) return;
                    expirationChanged.current = true;
                    setValue(
                      'expiresIn',
                      String(dayjs(date).startOf('day').diff(dayjs().startOf('day'), 'day'))
                    );
                    setCalendarOpened(false);
                  }}
                />
              </PopoverContent>
            </Popover>
          </div>
        </div>
        <div className="border-t border-border/50 pt-4">
          <div className="flex items-center justify-between gap-3">
            <button
              type="button"
              className="flex items-center gap-2 text-xs uppercase tracking-wide"
              aria-expanded={settingsOpened}
              aria-controls={settingsId}
              onClick={() => setSettingsOpened(!settingsOpened)}
            >
              <ChevronDown
                className={cn('h-4 w-4 transition-transform', settingsOpened && 'rotate-180')}
              />
              {t('sharedLinkDisplaySettings')}
              <span className="rounded bg-muted px-1.5 py-0.5 text-xs">
                {enabledCount}/{visibleSettings.length}
              </span>
            </button>
            <Switch
              checked={enabledCount === visibleSettings.length}
              aria-label={t('sharedLinkDisplaySettings')}
              onCheckedChange={(checked) => {
                for (const key of visibleSettings) setValue(key, checked);
                setValue('settings', checked);
              }}
            />
          </div>
          {settingsOpened && (
            <div id={settingsId} className="mt-3 space-y-3 pl-6">
              {visibleSettings.map((key) => (
                <Controller
                  key={key}
                  name={key}
                  control={control}
                  render={({ field }) => (
                    <div className="flex items-center justify-between gap-3 text-sm">
                      <span>{t(key)}</span>
                      <Switch
                        checked={!!field.value}
                        aria-label={t(key)}
                        onCheckedChange={(checked) => updateSetting(key, checked)}
                      />
                    </div>
                  )}
                />
              ))}
            </div>
          )}
        </div>
      </fieldset>
      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={onCancel} disabled={pending}>
          {t('cancel')}
        </Button>
        <Button type="submit" size="sm" disabled={pending} loading={pending} className="gap-1">
          {mode === 'create' && <PlusIcon className="!h-4 !w-4" />}
          {t(mode === 'edit' ? 'save-changes' : 'createShareLink')}
        </Button>
      </div>
    </form>
  );
}
